import React, { useEffect, useState } from "react";
import { Divider, makeStyles, tokens } from "@fluentui/react-components";
import { OutlineCategory } from "./OutlineCategory";
import { OutlineStyle } from "./OutlineStyle";
import { OutlineStylesContainer } from "./OutlineStylesContainer";
import "./CharacterOutlineList.css";

import type { CharacterOutlineGroup } from "src/features/selectors";
import type { CharStyleFormattingOrigin } from "src/services/character_styles/types";
import type { CharacterStyleProfileEntry, OutlineMode, StyleDefinition } from "src/types";

const useStyles = makeStyles({
  divider: {
    color: tokens.colorNeutralStroke2,
  },
});

export interface CharacterOutlineListProps {
  characterStyles?: CharacterStyleProfileEntry[] | null;
  expandAll: boolean;
  ignoreParagraphs?: boolean;
  outlineMode: OutlineMode;
  selectedCharacterOriginFilter?: CharStyleFormattingOrigin | null;
  selectedCharacterStyleFilter?: string | null;
  styleDefinitionMap?: Record<string, StyleDefinition> | null;
  styleGroups: CharacterOutlineGroup[];
  onClearCharacterStyle: () => void;
  onSelectCharacterStyle: (name: string | null, origin?: CharStyleFormattingOrigin | null) => void;
}

export function CharacterOutlineList(props: React.PropsWithChildren<CharacterOutlineListProps>) {
  const styles = useStyles();

  return (
    <div className="character-outline-list">
      {props.styleGroups.map((group, i) => (
        <React.Fragment key={group.id}>
          <CharacterOutlineGroupSection {...props} group={group} />
          {i < props.styleGroups.length - 1 && <Divider className={styles.divider} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function CharacterOutlineGroupSection({
  group,
  ...props
}: CharacterOutlineListProps & { group: CharacterOutlineGroup }) {
  const containsSelection = group.origin === props.selectedCharacterOriginFilter;
  const [isExpanded, setIsExpanded] = useState(containsSelection || props.expandAll);

  useEffect(() => {
    setIsExpanded(containsSelection || props.expandAll);
  }, [props.expandAll]);

  useEffect(() => {
    if (containsSelection) setIsExpanded(true);
  }, [containsSelection]);

  const toggle = () => setIsExpanded((prev) => !prev);

  return (
    <div className="character-outline-group">
      <OutlineCategory
        {...group}
        expanded={isExpanded}
        selected={false}
        onExpand={(e) => {
          e.stopPropagation();
          toggle();
        }}
        onSelectGroup={toggle}
        onClearGroup={() => {}}
        onSetBoundaryIndices={() => {}}
      />
      <OutlineStylesContainer expanded={isExpanded}>
        {group.styleGroup.map((sg) => (
          <OutlineStyle
            key={`${group.origin}-${sg.name}`}
            {...sg}
            characterStyles={props.characterStyles}
            ignoreParagraphs={props.ignoreParagraphs}
            outlineMode={props.outlineMode}
            selected={(style) => style === props.selectedCharacterStyleFilter && containsSelection}
            styleDefinitionMap={props.styleDefinitionMap}
            onClearCharacterStyle={props.onClearCharacterStyle}
            onSelectCharacterStyle={(name) => props.onSelectCharacterStyle(name, group.origin)}
          />
        ))}
      </OutlineStylesContainer>
    </div>
  );
}
